/** Display column names for headers-before-rows while a table payload is still loading. */

function uniq(cols) {
  const seen = new Set()
  const out = []
  for (const c of cols) {
    if (!c || seen.has(c)) continue
    seen.add(c)
    out.push(c)
  }
  return out
}

function withStatusFirst(cols) {
  const rest = cols.filter((c) => c !== 'Status')
  return cols.includes('Status') ? ['Status', ...rest] : cols
}

export const PLACEHOLDER_ARTIST_MAPPING = [
  'Artist',
  'Status',
  'Chartmetric ID',
  'TikTok',
  'TikTok UID',
  'Instagram',
  'Instagram UID',
  'YouTube',
  'SoundCloud',
  'Spotify',
  'Apple Music',
  'Genius',
  'Twitter',
  'Shazam',
  'YT Search Topic ID',
  'Updated At',
  'id',
]

export const PLACEHOLDER_ARTIST_MAPPING_DUPS = [
  'Dup Group',
  'Dup Role',
  'Dup Keys',
  ...PLACEHOLDER_ARTIST_MAPPING,
]

export const PLACEHOLDER_SOUND_MAPPING = [
  'TikTok Sound ID',
  'Status',
  'Artist Mapping ID',
  'Song Mapping ID',
  'Song Title Match',
  'Artist Name Match',
  'Matched Sound ID',
  'Matched Artist ID',
  'Is Sound User Artist?',
  'Sound Title',
  'Sound Author',
  'Album Name',
  'Language',
  'IG Reels Sound Title',
  'IG Reels Sound Artist/User Name',
  'IG Reels Sound Artist/User ID',
]

export const PLACEHOLDER_SNOOP_MAPPING = [
  'id',
  'IG User ID',
  'IG Username',
  'IG Full Name',
  'IG Following Count',
  'IG Follower Count',
  'Status',
  'Updated At',
]

export const PLACEHOLDER_P_LINE_MAPPING = ['Text', 'Partial']

export const PLACEHOLDER_LUMINATE_MAPPING = [
  'Entity Type',
  'Entity Key',
  'Status',
  'Title',
  'Primary Artist',
]

export const PLACEHOLDER_PGP_PERSON = [
  'Pub Mapping ID',
  'Name',
  'Status',
  'Credits TW',
  'Credit Details',
  'Chart',
  'Best Rank',
  'Genius Artist ID',
  'Genius URL',
]

export const PLACEHOLDER_PGP_CREDIT = [
  'Pub Mapping ID',
  'Name',
  'Status',
  'Credit Type',
  'Song',
  'Chart Artist',
  'Chart',
  'Rank',
  'Peak',
  'Spins TW',
  'Genius Artist ID',
  'Genius URL',
  'First Seen',
]

export const PLACEHOLDER_PGP_ROSTER = [
  'Pub Mapping ID',
  'Name',
  'Credit Type',
  'Song',
  'Primary Artist',
  'Release Date',
  'Genius Song ID',
  'Genius Artist ID',
  'Genius URL',
  'Lead A&R',
  'First Seen',
]

export const PLACEHOLDER_PGP_AGENDA = [
  'Pub Mapping ID',
  'Genius Artist ID',
  'Name',
  'Genius URL',
  'Status',
  'Lead A&R',
  'Lawyer',
  'Calc',
  'Committed $',
  'Uncommitted',
  'Option',
  'Action',
  'Notes',
  'Created At',
  'Updated At',
]

export const PLACEHOLDER_PGP_MAPPING = PLACEHOLDER_PGP_AGENDA

export const PLACEHOLDER_GENIUS_SONG_MAPPING = [
  'ID',
  'Chart Title',
  'Chart Artist',
  'Genius Song ID',
  'Genius Title',
  'Genius Primary Artist',
  'Resolution',
  'Search Attempts',
  'Last Search',
  'Credits Fetched',
  'First Seen',
  'Updated At',
]

const IG_USER = [
  'Artist',
  'Status',
  'Instagram',
  'IG User ID',
  'IG Follower Count',
  'Daily IG Follower Growth',
  'Daily IG Growth %',
  'Weekly IG Follower Growth',
  'Weekly IG Growth %',
  'Monthly IG Follower Growth',
  'Monthly IG Growth %',
  '90D Daily IG Growth Peak',
  'All-Time Daily IG Growth Peak',
]

const IG_POST = [
  'Instagram UID',
  '30D # IG Posts (max 12)',
  '30D Average IG Reels Engagement Rate (max 12)',
  '30D Median IG Reels Views (max 12)',
  '30D Median IG Non-Reels Engagement Rate (max 12)',
]

const TT_USER = [
  'Artist',
  'Status',
  'TikTok',
  'TikTok UID',
  'TT Follower Count',
  'Daily TT Follower Growth',
  'Daily TT Growth %',
  'Weekly TT Follower Growth',
  'Weekly TT Growth %',
  'Monthly TT Follower Growth',
  'Monthly TT Growth %',
  '90D Daily TT Growth Peak',
  'All-Time Daily TT Growth Peak',
]

const TT_POST = [
  '30D # TT Posts (max 20)',
  '30D Median TT Views (max 20)',
  '30D Average TT Engagement Rate (max 20)',
  '30D Average TT Saves Rate (max 20)',
]

export const PLACEHOLDER_IG_UNSIGNED = uniq([...IG_USER, ...IG_POST])
export const PLACEHOLDER_TT_UNSIGNED = uniq([...TT_USER, ...TT_POST])
export const PLACEHOLDER_IG_ROSTER = withStatusFirst(uniq([...IG_USER, ...IG_POST]))
export const PLACEHOLDER_TT_ROSTER = withStatusFirst(uniq([...TT_USER, ...TT_POST]))

export const PLACEHOLDER_TT_SOUND = [
  'TikTok Sound ID',
  'TikTok Sound Title',
  'TikTok Sound Artist',
  'Status',
  'Link',
  'TT Sound Creates',
  'Daily TT Sound Creates Growth',
  'Daily TT Sound Creates Growth %',
  'Weekly TT Sound Creates Growth',
  'Weekly TT Sound Creates Growth %',
  'Monthly TT Sound Creates Growth',
  'Monthly TT Sound Creates Growth %',
  '90D Daily TT Sound Creates Peak',
  'All-Time Daily TT Sound Creates Peak',
]

export const PLACEHOLDER_YT_ROSTER = withStatusFirst([
  'Artist',
  'YouTube ID',
  'YT Subscriber Count',
  'Daily YT Subs Growth',
  'Daily YT Growth %',
  'Weekly YT Subs Growth',
  'Weekly YT Growth %',
  'Monthly YT Subs Growth',
  'Monthly YT Growth %',
  '90D Daily YT Growth Peak',
])

export const PLACEHOLDER_TWITTER_ROSTER = withStatusFirst([
  'Artist',
  'username',
  'Twitter UID',
  'TW Follower Count',
  'Daily TW Follower Growth',
  'Weekly TW Follower Growth',
  'Weekly TW Growth %',
  'Monthly TW Follower Growth',
  'TW Like Count',
])

export const PLACEHOLDER_SPOTIFY_ROSTER = withStatusFirst([
  'Artist',
  'Spotify ID',
  'Spotify Follower Count',
  'Daily Spotify Followers Growth',
  'Daily Spotify Growth %',
  'Weekly Spotify Followers Growth',
  'Weekly Spotify Growth %',
  'Monthly Spotify Followers Growth',
  'Monthly Spotify Growth %',
  '90D Daily Spotify Growth Peak',
])

export const PLACEHOLDER_SNOOP_SUMMARY = [
  'Recent Count',
  'Prior Count',
  'New Follows',
  'IG Username',
  'IG User ID',
  'IG Full Name',
  'Link',
  'Verified',
  'Status',
  'Artist Status',
  'Artist',
  'Public Email',
  'New Follows Usernames',
  'All Follows Usernames',
]

export const PLACEHOLDER_LUMINATE_TRACKS = [
  'Status',
  'Track Title',
  'Display Artist',
  'Imprint',
  'Luminate Genres',
  'Luminate Song ID',
  'Streams TW',
  'Streams LW',
  '% Chg',
  'Streams ATD',
  'Residual',
  'Audio % TW',
  'Weeks since release',
  'Primary Artist',
  'Main Artist IDs',
  'Track Status',
  'Primary Artist Status',
  'Imprint Status',
]

export const PLACEHOLDER_LUMINATE_AGG = [
  'Status',
  'Primary Artist',
  'Track Count',
  'Imprint',
  'Luminate Genres',
  'Weeks Since Latest Release',
  'Total Streams TW',
  'Total Streams LW',
  '% Chg',
  'Total Streams ATD',
  'Max Residual',
  'Main Artist IDs',
  'Has Main Artist ID',
]

/** Matches zLuminate.ARTIST_MULTIWEEK_DASHBOARD_WEEKS; Week 1 = newest (left). */
export const LUMINATE_ARTIST_PLACEHOLDER_WEEKS = 7

export const PLACEHOLDER_LUMINATE_ARTISTS = [
  'Rank',
  'Name',
  'Luminate Artist ID',
  'Imprint',
  'In Track File',
  'Biggest Track Title',
  'Biggest Release Date',
  'Latest Track Title',
  'Latest Release Date',
  'Status',
  'Artist Type',
  'Country of Origin',
  'Language',
  'Focus Country-Lang',
  'Imprint DK/Self',
  'Score',
  'Gender',
  'Luminate Genres',
  '# Weeks Up',
  ...Array.from({ length: LUMINATE_ARTIST_PLACEHOLDER_WEEKS }, (_, i) => `Streams Week ${i + 1}`),
  'Streams ATD',
  'Stream Equivalent ATD',
  'Song Sales ATD',
]

// ---------------------------------------------------------------------------
// Operations (Catalog / Commitments / Process / Updates)
// ---------------------------------------------------------------------------

export const PLACEHOLDER_OPS_CATALOG = [
  'Encore ID',
  'Product Type',
  'Artist',
  'Title',
  'Release Date',
  'ISRC',
  'UPC',
  'Version',
  'Duration',
  'Copyrights',
  'Notes',
  'OG Release Date',
  'Partner',
  'C/P Line',
  'Box Folder ID',
  'Updated At',
]

export const PLACEHOLDER_OPS_PROCESS = [
  'Project',
  'Status',
  'Scope',
  'Type',
  'Frequency',
  'Automation',
  'Category',
  'Lead',
  'Sub-Lead',
  'Notes',
  'Sort Order',
  'Updated At',
]

export const PLACEHOLDER_OPS_COMMITMENTS = [
  'Artist',
  'Luminate Artist ID',
  'Tracks Committed',
  'Tracks Released',
  'Tracks Remaining',
  'Contract Start',
  'Period End',
  'Days Elapsed',
  'Days Remaining',
  'Option Period',
  'License Extend',
  'Time Commitment (Months)',
  'Tracks Tail (Months)',
  'Match Length (Months)',
  'License Length (Years)',
  'Updated At',
]

export const PLACEHOLDER_OPS_UPDATES = [
  'Timestamp',
  'User',
  'Dashboard',
  'Action',
  'Artist',
  'Title',
  'Entity',
  'Detail',
]

export const PLACEHOLDER_OPS_ASSETS = [
  'Artist',
  'Release',
  'Bucket',
  'Added',
  'Agreements',
  'Side Artist',
  'Credits',
  'Sample',
  'Master',
  'Atmos',
  'Stems',
  'Lyrics',
]

export const PLACEHOLDER_OPS_CREATIVE = [
  'Artist',
  'Release',
  'Bucket',
  'Added',
  'Cover Art',
  'Canvas',
  'Banners',
  'Video',
  'Photos',
  'Physical',
]

export const PLACEHOLDER_OPS_COMMITMENTS_MAPPING = [
  'Artist',
  'Source',
  'Title',
  'Release Date',
  'Release Date Override',
  'Attribution Status',
  'Match Path',
  'Song ID',
  'Song Luminate ID',
  'Song Display Artist',
  'Notes',
]

export const PLACEHOLDER_OPS_COMMITMENTS_SIMPLIFY = [
  'Artist',
  'Commitment Status',
  'License/Match Status',
  'Commitment Urgency',
]
